import { useCallback, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';
import { useFocusEffect } from '@react-navigation/native';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { ModularScreens, ModularScreensArray } from 'router/types';

import { navigationRef } from 'utils/navigation/navigationUtils';
import { isPhone, isTablet, isWeb } from 'utils/responsiveness';

import { EpgFilterCategory } from 'screens/Epg/types';

const {
    EPG: { FILTER_CATEGORIES },
} = LANGUAGE_KEYS;

function useFiltering(screenReaderEnabled: boolean) {
    const { t } = useTranslation();

    const filterBottomSheetRef = useRef<BottomSheetModalMethods>(undefined);
    const initialFilterCategories = useRef(
        Object.keys(FILTER_CATEGORIES).map<EpgFilterCategory>((key) => ({
            key: key as keyof typeof FILTER_CATEGORIES,
            label: t(FILTER_CATEGORIES[key as keyof typeof FILTER_CATEGORIES]),
            isSelected: screenReaderEnabled
                ? key === FILTER_CATEGORIES.EPG_FILTER_SUBSCRIBED
                : key === FILTER_CATEGORIES.EPG_FILTER_ORIGINAL,
        })),
    );

    const [isFilterListVisible, setIsFilterListVisible] = useState(false);
    const [filterCategories, setFilterCategories] = useState(
        initialFilterCategories.current,
    );

    const selectedFilterCategory = useMemo(
        () => filterCategories.find(({ isSelected }) => isSelected),
        [filterCategories],
    );

    const filterCategorySelected = useMemo(
        () =>
            filterCategories.some(
                ({ key, isSelected }) =>
                    key !== FILTER_CATEGORIES.EPG_FILTER_ORIGINAL && isSelected,
            ),
        [filterCategories],
    );

    const onShowFilterList = useCallback(() => {
        if (isTablet || isWeb || (isPhone && screenReaderEnabled)) {
            setIsFilterListVisible(true);
        } else if (filterBottomSheetRef.current) {
            filterBottomSheetRef.current?.present();
            setIsFilterListVisible(true);
        }
    }, [screenReaderEnabled]);

    const onCloseFilterList = useCallback(() => {
        if (isTablet || isWeb || (isPhone && screenReaderEnabled)) {
            setIsFilterListVisible(false);
        } else if (filterBottomSheetRef.current) {
            filterBottomSheetRef.current?.dismiss();
            setIsFilterListVisible(false);
        }
    }, [screenReaderEnabled]);

    const onSelectFilterCategory = useCallback(
        (filterCategory: EpgFilterCategory) => {
            onCloseFilterList();

            setFilterCategories((_filterCategories) =>
                _filterCategories.map((_filterCategory) => ({
                    ..._filterCategory,
                    isSelected: _filterCategory.key === filterCategory.key,
                })),
            );
        },
        [onCloseFilterList],
    );

    useFocusEffect(
        useCallback(() => {
            return () => {
                const routeName =
                    navigationRef.current?.getCurrentRoute()?.name;

                if (
                    routeName &&
                    ModularScreensArray.includes(routeName as ModularScreens)
                ) {
                    setFilterCategories(initialFilterCategories.current);
                }
            };
        }, []),
    );

    return {
        selectedFilterCategory,
        filterBottomSheetRef,
        isFilterListVisible,
        filterCategories,
        filterCategorySelected,
        onSelectFilterCategory,
        onShowFilterList,
        onCloseFilterList,
    };
}

export default useFiltering;
